import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";

const THEME_KEY = "@chatnotesTheme";

const ThemeContext = createContext(null);

function getSystemDark() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

function loadStoredTheme() {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === "light" || stored === "dark" || stored === "system")
      return stored;
  } catch (e) {}
  return "system";
}

function getEffectiveTheme(preference) {
  if (preference === "light") return "light";
  if (preference === "dark") return "dark";
  return getSystemDark() ? "dark" : "light";
}

export default function ThemeProvider({ children }) {
  const [themePreference, setThemePreferenceState] = useState(loadStoredTheme);
  const [effectiveTheme, setEffectiveTheme] = useState(() =>
    getEffectiveTheme(loadStoredTheme())
  );
  const onSettingsChangeRef = useRef(null);

  const setThemePreference = (value) => {
    if (value !== "system" && value !== "light" && value !== "dark") return;
    setThemePreferenceState(value);
    try {
      localStorage.setItem(THEME_KEY, value);
    } catch (e) {}
    if (onSettingsChangeRef.current) {
      onSettingsChangeRef.current({ theme: value });
    }
  };

  const setThemePreferenceFromServer = (value) => {
    if (value !== "system" && value !== "light" && value !== "dark") return;
    setThemePreferenceState(value);
    try {
      localStorage.setItem(THEME_KEY, value);
    } catch (e) {}
  };

  const setOnSettingsChange = (cb) => {
    onSettingsChangeRef.current = cb;
  };

  const toggleTheme = () => {
    const next =
      getEffectiveTheme(themePreference) === "dark" ? "light" : "dark";
    setThemePreference(next);
  };

  useEffect(() => {
    setEffectiveTheme(getEffectiveTheme(themePreference));
  }, [themePreference]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    if (!media) return;
    const handler = () => {
      if (themePreference === "system") {
        setEffectiveTheme(media.matches ? "dark" : "light");
      }
    };
    media.addEventListener("change", handler);
    return () => media.removeEventListener("change", handler);
  }, [themePreference]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", effectiveTheme);
  }, [effectiveTheme]);

  const value = {
    themePreference,
    setThemePreference,
    setThemePreferenceFromServer,
    setOnSettingsChange,
    effectiveTheme,
    isDark: effectiveTheme === "dark",
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

export function initThemeFOUC() {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    const pref = stored === "light" || stored === "dark" ? stored : null;
    const dark = pref
      ? pref === "dark"
      : window.matchMedia("(prefers-color-scheme: dark)").matches;
    document.documentElement.setAttribute(
      "data-theme",
      dark ? "dark" : "light"
    );
  } catch (e) {}
}
